import { useState } from 'react'
import { useForm, Link } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput, parsePhone, formatFullPhone } from '@/components/ui/phone-input'
import { ArrowLeft } from 'lucide-react'

interface Props {
  idempotency_key: string
  defaults: Partial<FormData>
  errors?: Record<string, string>
}

interface FormData {
  organization_name: string
  business_name: string
  owner_name: string
  owner_phone: string
  address: string
  timezone: string
  idempotency_key: string
}

export default function New({ idempotency_key, defaults, errors = {} }: Props) {
  const parsedPhone = parsePhone(defaults.owner_phone || '')
  const [countryCode, setCountryCode] = useState(parsedPhone.countryCode)
  const [localPhone, setLocalPhone] = useState(parsedPhone.localPhone)
  const { data, setData, transform, post, processing } = useForm<FormData>({
    organization_name: defaults.organization_name || '',
    business_name: defaults.business_name || '',
    owner_name: defaults.owner_name || '',
    owner_phone: defaults.owner_phone || '',
    address: defaults.address || '',
    timezone: defaults.timezone || 'America/Mexico_City',
    idempotency_key,
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const ownerPhone = formatFullPhone(countryCode, localPhone)
    setData('owner_phone', ownerPhone)
    transform((form) => ({
      organization: { ...form, owner_phone: ownerPhone },
    } as unknown as FormData))
    post('/admin/organizations')
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-2xl space-y-6">
        <Link href="/admin/organizations" className={buttonVariants({ variant: 'ghost', size: 'sm' })}><ArrowLeft className="mr-1 h-4 w-4" /> Volver</Link>
        <Card>
          <CardHeader>
            <CardTitle>Preparar nueva organización</CardTitle>
            <CardDescription>Crea la cuenta, la primera sucursal y una invitación segura para la persona propietaria.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <Field id="organization_name" label="Organización" error={errors.organization_name}>
                <Input id="organization_name" value={data.organization_name} onChange={(e) => setData('organization_name', e.target.value)} required autoFocus />
              </Field>
              <Field id="business_name" label="Primera sucursal" error={errors.business_name} hint="Si lo dejas vacío, usaremos el nombre de la organización.">
                <Input id="business_name" value={data.business_name} onChange={(e) => setData('business_name', e.target.value)} />
              </Field>
              <Field id="owner_name" label="Nombre de la persona propietaria" error={errors.owner_name}>
                <Input id="owner_name" value={data.owner_name} onChange={(e) => setData('owner_name', e.target.value)} required />
              </Field>
              <Field id="owner_phone" label="WhatsApp de la persona propietaria" error={errors.owner_phone}>
                <PhoneInput
                  id="owner_phone"
                  countryCode={countryCode}
                  localPhone={localPhone}
                  onCountryCodeChange={setCountryCode}
                  onLocalPhoneChange={setLocalPhone}
                />
              </Field>
              <Field id="address" label="Dirección" error={errors.address}>
                <Input id="address" value={data.address} onChange={(e) => setData('address', e.target.value)} />
              </Field>
              {errors.base && <p className="text-sm text-destructive">{errors.base}</p>}
              <Button type="submit" disabled={processing} className="w-full">
                {processing ? 'Preparando...' : 'Crear organización e invitación'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  )
}

function Field({ id, label, error, hint, children }: { id: string; label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
